import type { Cloneable } from '@bindoc-projects/util-domain-lib';
import { BdEquals } from '@bindoc-projects/util-domain-lib';
import { AysClinicSn } from './clinic-sn';

/**
 * Identity for the SN-only clinics-api surface.
 *
 * Identity is the opaque `clinicSn` string plus an optional report `year`
 * (the Display endpoints default to the latest QB year when `year` is omitted).
 * `ik` is carried for display only and never participates in identity.
 *
 * Canonical string form is `${clinicSn}_${year}` (or just `clinicSn` when no
 * year is set). Neither `clinicSn` form contains `_`, so the separator is safe.
 */
export class AysClinicSnIdentifier implements Cloneable<AysClinicSnIdentifier> {
  private static readonly YEAR_PATTERN = /^20\d{2}$/;

  /**
   * `770001001` --> { clinicSn: '770001001' }
   * `770001001_2024` --> { clinicSn: '770001001', year: 2024 }
   * `L-260970082-1_2024` --> { clinicSn: 'L-260970082-1', year: 2024 }
   *
   * Throws on legacy composite strings (`ik_sn` / `ik_sn_year`) so they are
   * never silently mis-parsed — convert those explicitly via AysClinicIdentifier.
   */
  public static fromString(idString: string): AysClinicSnIdentifier {
    if (!idString) {
      throw new TypeError('AysClinicSnIdentifier.fromString: empty id string');
    }

    const parts = idString.split('_');

    if (parts.length >= 3) {
      throw new TypeError(
        `AysClinicSnIdentifier.fromString: '${idString}' looks like a legacy composite id — use AysClinicIdentifier.fromString`,
      );
    }

    if (parts.length === 2) {
      if (!this.YEAR_PATTERN.test(parts[1])) {
        throw new TypeError(
          `AysClinicSnIdentifier.fromString: '${idString}' second segment is not a report year — refusing to parse as clinicSn_year`,
        );
      }

      return new AysClinicSnIdentifier(parts[0], parseInt(parts[1], 10));
    }

    return new AysClinicSnIdentifier(parts[0]);
  }

  public static fromObj(obj: {
    clinicSn: string;
    year?: string | number;
    ik?: string | number;
  }): AysClinicSnIdentifier {
    return new AysClinicSnIdentifier(
      obj.clinicSn.toString(),
      obj.year !== undefined && obj.year !== null
        ? parseInt(obj.year.toString(), 10)
        : undefined,
      obj.ik !== undefined && obj.ik !== null ? obj.ik.toString() : undefined,
    );
  }

  /**
   * Bridge from the legacy `(ik, sn[, year])` identity that persisted payloads,
   * saved filters and route params still carry (BDA-5809 step 3).
   *
   * The clinicSn is resolved with {@link AysClinicSn.fromIdentifier}, so an `sn`
   * slot that ALREADY holds an opaque clinicSn is passed through rather than being
   * re-encoded — search-result ids flow back in this way. `ik` is kept for display
   * and is what the legacy backends fall back to when the clinicSn carries no
   * institution key.
   */
  public static fromLegacy(id: {
    ik: string | number;
    sn: string | number;
    year?: number;
  }): AysClinicSnIdentifier {
    return new AysClinicSnIdentifier(
      AysClinicSn.fromIdentifier(id.ik, id.sn),
      id.year,
      id.ik?.toString(),
    );
  }

  constructor(
    public clinicSn: string,
    public year?: number,
    /** display only — excluded from identity */
    public ik?: string,
  ) {}

  public toString(): string {
    if (!this.clinicSn) {
      return null;
    }

    return this.year ? `${this.clinicSn}_${this.year}` : this.clinicSn;
  }

  public clone(): AysClinicSnIdentifier {
    return new AysClinicSnIdentifier(this.clinicSn, this.year, this.ik);
  }

  /** Identity equality: clinicSn + year. `ik` is ignored. */
  public equals(other: AysClinicSnIdentifier): boolean {
    return !!this && !!other && this.toString() === other.toString();
  }

  /** clinicSn-only equality, ignoring year. */
  public equalsBySn(other: AysClinicSnIdentifier): boolean {
    return !!this && !!other && BdEquals.equals(this.clinicSn, other.clinicSn);
  }
}
